import React from "react";
import { InlineMath } from "react-katex";
import NumberLine from "../../../../components/NumberLine/NumberLine";

function randInt(min, max) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function pickShiftedWindow(target, minLimit, maxLimit) {
  const position = randInt(2, 8);
  let min = target - position;
  min = Math.max(minLimit, Math.min(maxLimit - 10, min));
  return { min, max: min + 10 };
}

function makeTask(type, target, min, max, labelTicks, note) {
  return {
    type,
    data: {
      target,
      min,
      max,
      labelTicks,
      note,
    },
  };
}

function ExerciseNumberLine({ task }) {
  const [selected, setSelected] = React.useState(null);
  return (
    <NumberLine
      min={task.data.min}
      max={task.data.max}
      target={task.data.target}
      selected={selected}
      onChange={setSelected}
      labelTicks={task.data.labelTicks}
    />
  );
}

function positiveBasic() {
  const target = randInt(2, 5);
  return makeTask(
    "IntegerNumberLinePositiveBasicTask",
    target,
    -6,
    6,
    [0, 1],
    "Liczby dodatnie leżą na prawo od zera."
  );
}

function negativeBasic() {
  const target = randInt(-5, -1);
  return makeTask(
    "IntegerNumberLineNegativeBasicTask",
    target,
    -6,
    6,
    [0, 1],
    "Liczby ujemne leżą na lewo od zera."
  );
}

function positiveShifted() {
  const target = randInt(7, 15);
  const { min, max } = pickShiftedWindow(target, 2, 18);
  return makeTask(
    "IntegerNumberLinePositiveShiftedTask",
    target,
    min,
    max,
    [min + 1, Math.round((min + max) / 2), max - 1],
    "Na przesuniętej osi nadal liczymy kolejne kreski co 1."
  );
}

function negativeShifted() {
  const target = randInt(-15, -7);
  const { min, max } = pickShiftedWindow(target, -18, -2);
  return makeTask(
    "IntegerNumberLineNegativeShiftedTask",
    target,
    min,
    max,
    [min + 1, Math.round((min + max) / 2), max - 1],
    "Im dalej w lewo, tym liczby są mniejsze."
  );
}

export function generateTasks() {
  return [positiveBasic(), negativeBasic(), positiveShifted(), negativeShifted()];
}

export function renderTask({ data }) {
  return (
    <p>
      Zaznacz na osi liczbowej liczbę <InlineMath math={`${data.target}`} />.
    </p>
  );
}

export function renderExerciseTask(task) {
  return (
    <div>
      {renderTask(task)}
      <ExerciseNumberLine task={task} />
    </div>
  );
}

export function renderInput(task, userAnswer, onChange, disabled) {
  const selected = typeof userAnswer?.value === "number" ? userAnswer.value : null;
  return (
    <NumberLine
      min={task.data.min}
      max={task.data.max}
      target={task.data.target}
      selected={selected}
      onChange={(value) => onChange({ value })}
      locked={disabled}
      labelTicks={task.data.labelTicks}
    />
  );
}

export function checkAnswer({ data }, userAnswer) {
  const value = userAnswer?.value;
  return {
    isCorrect: value === data.target,
    score: value === data.target ? 1 : 0,
    maxScore: 1,
  };
}

export function renderUserAnswer(userAnswer, task) {
  const value = typeof userAnswer?.value === "number" ? userAnswer.value : null;
  const selectedText =
    value === null ? <i>(nie zaznaczono)</i> : <InlineMath math={`${value}`} />;

  return (
    <div>
      <div style={{ marginBottom: 8 }}>Zaznaczono: {selectedText}</div>
      <NumberLine
        min={task.data.min}
        max={task.data.max}
        target={task.data.target}
        selected={value}
        locked
        labelTicks={task.data.labelTicks}
        validation={{ correctValue: task.data.target }}
      />
    </div>
  );
}

export const hideCorrectAnswerWhenUserAnswer = true;

export function renderAnswer({ data }) {
  return (
    <div>
      <div style={{ marginBottom: 8 }}>
        Liczba <InlineMath math={`${data.target}`} /> znajduje się w zaznaczonym miejscu na osi.
      </div>
      <NumberLine
        min={data.min}
        max={data.max}
        selected={data.target}
        locked
        labelTicks={data.labelTicks}
        showSelectedLabel
      />
    </div>
  );
}

export function renderExplanation({ data }) {
  return (
    <div>
      <p>{data.note}</p>
      <p>
        W tej osi korzystamy z podziałki co <InlineMath math={"1"} />, więc każda
        kolejna kreska oznacza zmianę o <InlineMath math={"1"} />. Liczby większe
        są po prawej stronie, a mniejsze po lewej.
      </p>
    </div>
  );
}

const taskAdapter = {
  renderTask,
  renderInput,
  checkAnswer,
  renderAnswer,
  renderUserAnswer,
  renderExplanation,
  hideCorrectAnswerWhenUserAnswer,
};

export const registry = {
  IntegerNumberLinePositiveBasicTask: taskAdapter,
  IntegerNumberLineNegativeBasicTask: taskAdapter,
  IntegerNumberLinePositiveShiftedTask: taskAdapter,
  IntegerNumberLineNegativeShiftedTask: taskAdapter,
};
